import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const fadeUp = { 
  hidden: { opacity: 0, y: 30 }, 
  show: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  } 
};

const stagger = { 
  show: { 
    transition: { 
      staggerChildren: 0.15,
      delayChildren: 0.2
    } 
  } 
};

const Hero = ({ onOpenAssessment }) => {
  const images = [
    "/hero.jpg",
    "/hero2.jpg",
    "/hero3.jpg",
    "/hero4.jpg",
    "/hero5.jpg",
    "/hero6.jpg"
  ];

  const [randomImage, setRandomImage] = useState(images[0]);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isHovered) {
        const index = Math.floor(Math.random() * images.length);
        setRandomImage(images[index]);
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [isHovered]);

  return (
    <section id="top" className="relative min-h-screen overflow-hidden flex items-center">
      {/* Original background layers */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-indigo-800 z-0" />
      <div className="absolute -top-16 -left-16 w-56 h-56 bg-yellow-400/30 rounded-full blur-2xl z-0" />
      <div className="absolute bottom-10 -right-10 w-72 h-72 bg-white/10 rounded-full blur-xl z-0" />

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-24 text-white z-5">
        <motion.div 
          variants={stagger} 
          initial="hidden" 
          animate="show" 
          className="grid md:grid-cols-2 gap-12 items-center"
        >
          <motion.div variants={fadeUp} className="max-w-2xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              Unlock Your Child's <span className="text-yellow-300 bg-clip-text text-transparent bg-gradient-to-r from-yellow-300 to-amber-400">True Potential</span> in Maths & Tech
            </h1>
            <p className="text-lg md:text-xl mt-6 text-indigo-100 leading-relaxed">
              From elementary mathematics to coding and future-ready skills, we guide every learner to success — starting with a{" "}
              <span className="font-bold text-yellow-300">free personalized assessment</span>.
            </p>
            
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
                <Link
                  to="/register-student"
                  className="inline-flex items-center px-6 py-4 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-semibold rounded-xl shadow-lg hover:shadow-yellow-500/30 transition-all duration-300"
                >
                  <span>Take the Free Maths Assessment</span>
                  <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                  </svg>
                </Link>
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
                <a
                  href="#programs"
                  className="inline-flex items-center px-6 py-4 border-2 border-white/30 rounded-xl hover:bg-white/10 transition-all duration-300"
                >
                  <span>Explore Programs</span>
                  <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </a>
              </motion.div>
            </div>
            
            <div className="mt-6 flex items-center text-sm text-indigo-100/90">
              <svg className="w-5 h-5 mr-2 text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
              <span>No payment required • Instant guidance for parents</span>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex justify-center mt-10 md:mt-0"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className="relative">
              {/* Phone Frame with shadow and reflection effect */}
              <div className="relative w-full max-w-sm aspect-[9/16] bg-gray-900 rounded-3xl shadow-2xl overflow-hidden border-8 border-gray-800 transform rotate-3">
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent z-10"></div>
                <img
                  src={randomImage}
                  alt="Learning preview"
                  className="w-full h-full object-cover transition-opacity duration-1000"
                />
                
                {/* Screen notch */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1/3 h-5 bg-gray-800 rounded-b-lg z-20"></div>
              </div>
              
              {/* Floating elements with animations */}
              <div className="absolute -top-6 -right-6 w-14 h-14 bg-yellow-400 rounded-full opacity-80">
                <div className="flex items-center justify-center h-full">
                  <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
              </div>
              
              <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-indigo-500 rounded-full opacity-80">
                <div className="flex items-center justify-center h-full">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                  </svg>
                </div>
              </div>
              
              {/* Glow effect behind phone */}
              <div className="absolute -inset-4 bg-yellow-400/20 rounded-3xl blur-xl -z-10"></div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Original wave divider */}
      <div className="absolute bottom-0 left-0 w-full z-0 overflow-hidden">
        <svg 
          viewBox="0 0 1440 320" 
          className="w-full h-16 md:h-24 lg:h-32"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="gradientWave" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: "#2563EB", stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: "#EAB308", stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <path
            fill="url(#gradientWave)"
            d="M0,256L80,240C160,224,320,192,480,192C640,192,800,224,960,234.7C1120,245,1280,235,1360,229.3L1440,224V320H0Z"
          ></path>
        </svg>
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="flex flex-col items-center text-indigo-100/70"
        >
          <span className="text-sm mb-2">Scroll to explore</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
          </svg>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;